<template>
  <div class="mt-4">
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-1/2 mx-auto">
      <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">
              Package Tender
            </h1>

            <p class="text-sm text-gray-600 mt-1">
              {{ project?.name }} · {{ packageModel?.name }}
            </p>

            <p
              v-if="supplier"
              class="text-sm text-gray-500"
            >
              {{ supplier.name }} · Revision {{ tender?.price_revision }}
            </p>
          </div>

          <div class="flex items-center gap-3">
            <button
              v-if="canEdit"
              type="button"
              class="btn btn--primary-blue"
              :disabled="submitAction.loading.value"
              data-test="submit-tender"
              @click="submitTender"
            >
              Submit Tender
            </button>

            <span
              v-if="tender"
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="tender.statusBadgeClass"
            >
              {{ tender.statusTitle }}
            </span>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Item
              </th>

              <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Quantity
              </th>

              <th
                v-if="isScheduleOfRates"
                class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase"
              >
                Rate
              </th>

              <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Amount
              </th>
            </tr>
          </thead>

          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(row, index) in rows"
              :key="row.id"
            >
              <td class="px-3 py-2 text-sm text-gray-900">
                {{ row.trade_item?.name }}
              </td>

              <td class="px-3 py-2 text-sm text-right text-gray-700">
                {{ quantityFor(row) }} {{ row.trade_item?.unit }}
              </td>

              <td
                v-if="isScheduleOfRates"
                class="px-3 py-2 text-right"
              >
                <AppCurrencyInput
                  v-if="row.package_tender_items.length > 0"
                  ref="itemInputRefs"
                  v-model="row.package_tender_items[0].rate"
                  :name="`rate-${row.id}`"
                  class="form-input w-28 ml-auto"
                  :disabled="!canEdit"
                  @blur="saveItem(row.package_tender_items[0], index)"
                  @keyup.enter.prevent="$event.target.blur()"
                />
              </td>

              <td class="px-3 py-2 text-right">
                <template v-if="!isScheduleOfRates">
                  <AppCurrencyInput
                    v-if="row.package_tender_items.length > 0"
                    ref="itemInputRefs"
                    v-model="row.package_tender_items[0].amount"
                    :name="`amount-${row.id}`"
                    class="form-input w-28 ml-auto"
                    :disabled="!canEdit"
                    @blur="saveItem(row.package_tender_items[0], index)"
                    @keyup.enter.prevent="$event.target.blur()"
                  />

                  <span
                    v-else
                    class="text-sm text-gray-700"
                  >
                    Not part of tender at this revision
                  </span>
                </template>

                <span
                  v-else-if="isScheduleOfRates"
                  class="text-sm text-gray-700"
                >
                  {{ row.package_tender_items[0] ? $tcc(calculateAmount(row.package_tender_items[0], row)) : '' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type CurrencyInput from '@/components/app/CurrencyInput.vue';
import { useSignedData } from '@/composables/useSignedData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, falsey, parseUrlParams } from '@/helpers';
import PackageTenderItem from '@/models/PackageTenderItem';
import PackageTradeItem from '@/models/PackageTradeItem';
import { usePackagesStore } from '@/store/packages';
import { usePackageTenderItemsStore } from '@/store/packageTenderItems';
import { usePackageTendersStore } from '@/store/packageTenders';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useProjectsStore } from '@/store/projects';
import { useSuppliersStore } from '@/store/supplier';
import { useTemplateRefsList } from '@vueuse/core';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

const props = defineProps<{
  tenderId: string;
}>();

const signed = useSignedData();
const packageTendersStore = usePackageTendersStore();
const packageTenderItemsStore = usePackageTenderItemsStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const packagesStore = usePackagesStore();
const projectsStore = useProjectsStore();
const suppliersStore = useSuppliersStore();
const storeItemAction = useStoreApiAction(packageTenderItemsStore.store);
const submitAction = useStoreApiAction(packageTendersStore.submit);

if(signed?.models?.package_tender) {
  packageTendersStore.models.save(signed.models.package_tender);
}

if(signed?.models?.package_tender_items) {
  packageTenderItemsStore.models.save(signed.models.package_tender_items);
}

if(signed?.models?.package) {
  packagesStore.models.save(signed.models.package);
}

if(signed?.models?.package_trade_items) {
  packageTradeItemsStore.models.save(signed.models.package_trade_items);
}

if(signed?.models?.project) {
  projectsStore.models.save(signed.models.project);
}

if(signed?.models?.supplier) {
  suppliersStore.models.save(signed.models.supplier);
}

const tender = computed(() => {
  return packageTendersStore.models.find(parseInt(props.tenderId));
});

const packageModel = computed(() => {
  if(falsey(tender.value?.package_id)) {
    return undefined;
  }

  return packagesStore.models.find(tender.value.package_id);
});

const project = computed(() => {
  if(falsey(packageModel.value?.project_id)) {
    return undefined;
  }

  return projectsStore.models.find(packageModel.value.project_id);
});

const supplier = computed(() => {
  if(falsey(tender.value?.supplier_id)) {
    return undefined;
  }

  return suppliersStore.models.find(tender.value.supplier_id);
});

const canEdit = computed(() => tender.value?.status === 'issued');
const isScheduleOfRates = computed(() => packageModel.value?.procurement_method === 'schedule_of_rates');
const saveItemParams = computed(() => parseUrlParams(signed?.meta?.save_item_url as string));
const submitParams = computed(() => parseUrlParams(signed?.meta?.submit_url as string));

const rows = computed(() => {
  if(falsey(tender.value)) {
    return [];
  }

  return packageTradeItemsStore.models
    .where('package_id', tender.value.package_id)
    .with('trade_item')
    .with('package_tender_items', (query) => query.where('package_tender_id', tender.value.id))
    .get();
});

const quantityFor = (item: PackageTradeItem): number | undefined => {
  if(!falsey(item.quantity)) {
    return item.quantity;
  }

  if(!falsey(item.trade_item?.quantity)) {
    return item.trade_item?.quantity;
  }

  return undefined;
};

const calculateAmount = (item: PackageTenderItem, packageTradeItem: PackageTradeItem) => {
  // if amount is set via submission, then pull it through
  if(!falsey(item.amount)) {
    return item.amount;
  }

  const quantity = quantityFor(packageTradeItem);

  if(!falsey(item.rate) && !falsey(quantity)) {
    return item.rate * quantity;
  }

  return undefined;
};

const itemInputRefs = useTemplateRefsList<typeof CurrencyInput>();

const focusNextRow = (index: number) => {
  for(let nextIndex = index + 1;nextIndex < rows.value.length;nextIndex++) {
    const nextInput = itemInputRefs.value[nextIndex];

    if(nextInput?.focusInput) {
      nextInput.focusInput();

      return;
    }
  }
};

const saveItem = (item: PackageTenderItem, index: number) => {
  if(!canEdit.value || falsey(tender.value) || falsey(item)) {
    return;
  }

  const payload: {
    package_trade_item_id: number;
    rate?: number;
    amount?: number;
  } = {
    package_trade_item_id: item.package_trade_item_id,
  };

  if(isScheduleOfRates.value) {
    if(falsey(item.rate)) {
      return;
    }

    payload.rate = item.rate;
  } else {
    if(falsey(item.amount)) {
      return;
    }

    payload.amount = item.amount;
  }

  nextTick(() => focusNextRow(index));
  storeItemAction.request(item.package_tender_id, saveItemParams.value, payload);
};

const submitTender = () => {
  if(falsey(tender.value)) {
    return;
  }

  submitAction.request(tender.value.id, submitParams.value).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Tender submitted',
    });
  }).catch((error: any) => {
    Swal.fire({
      icon: 'error',
      title: 'Failed to submit tender',
      text: error?.data?.message,
    });
  });
};
</script>
